import React from 'react';
import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch } from '../../app/hook';
import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import ProBadge from '../../components/ui/ProBadge';
import usePermission from '../../hooks/usePermission';
import AppearanceCustomCss from './AppearanceCustomCss';
import AppearanceCustomPoweredBy from './AppearanceCustomPoweredBy';
import AppearanceMessageNotificationComponent from './AppearanceMessageNotification.component';
import { handleResetAppearance, handleUpdateAppearanceItem } from './appearanceSlice';
import AppearanceTheme from './AppearanceTheme';
import AppearanceVoiceResponseToggle from './AppearanceVoiceResponseToggle';
import { IAiAppearance } from './types';

const AppearanceCallBot = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();

  const {
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useFormContext<IAiAppearance>();

  const formContext = {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
    control,
  };

  // Popover voice and texts
  const voiceCommandPopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-14'),
  };

  const SaveConversationData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-08'),
  };

  const SaveStatsData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-09'),
  };

  return (
    <div className="appearance-shared-ai tw-w-full">
      <React.Fragment>
        {/* Theme */}
        <AppearanceTheme />

        <div className={'tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex tw-mt-16'}>
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">Notifications</div>
        </div>

        <AppearanceMessageNotificationComponent register={register} watch={watch} errors={errors} control={control} />

        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Voice Messages */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t('DashboardSettingCustomizeChat/l-16')}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
            <AppearanceVoiceResponseToggle formContext={formContext} i18n={i18n} permissions={permissions} />
          </div>
        </div>
        <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

        {/* Other */}
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t('DashboardSettingCustomizeChat/other')}
          </div>
          <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
            <Form.Group className="tw-w-full  tw-flex  tw-flex-col ">
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('DashboardSettingCustomizeChat/l-19')}
                {!permissions.settingsPage.widget.customization.displayAfter && (
                  <ProBadge
                    className="tw-ms-2"
                    onClose={() => {
                      dispatch(handleResetAppearance());
                    }}
                  />
                )}
              </Form.Label>

              <Form.Control
                isInvalid={!!errors.displayAfter}
                maxLength={30}
                max={300}
                disabled={!permissions.settingsPage.widget.customization.displayAfter}
                {...register('displayAfter', {
                  onChange: event => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'displayAfter',
                        value: event.target.value != '' ? event.target.value : null,
                      })
                    );
                  },
                })}
                placeholder={'0'}
                type={'number'}
                className="tw-h-10 tw-w-32 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
              />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors?.displayAfter?.message as string}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={!permissions.settingsPage.widget.customization.save_conversation}
                type="switch"
                id="save_conversation"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/t-08')}
                isInvalid={!!errors.save_conversation}
                feedbackType="invalid"
                feedback={errors.save_conversation?.message}
                {...register('save_conversation', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'save_conversation',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.save_conversation && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
              <PopoverWithAudio
                data={{
                  contentText: SaveConversationData.contentText,
                }}
              />
            </Form.Group>

            <AppearanceCustomPoweredBy
              hasPermission={permissions.settingsPage.widget.customization.poweredBy}
              isInvalid={!!errors.customPoweredBy}
              errors={errors.customPoweredBy}
              setFormValue={setValue}
              register={register}
              watch={watch}
            />

            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                type="switch"
                id="saveStats"
                disabled={!permissions.settingsPage.widget.customization.saveStats}
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/l-17')}
                isInvalid={!!errors.saveStats}
                feedbackType="invalid"
                feedback={errors.saveStats?.message}
                {...register('saveStats', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'saveStats',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.saveStats && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}

              <PopoverWithAudio
                data={{
                  contentText: SaveStatsData.contentText,
                }}
              />
            </Form.Group>

            <AppearanceCustomCss />
          </div>
        </div>
      </React.Fragment>
    </div>
  );
};

export default WrappedErrorBoundary(AppearanceCallBot);
